import { type FC, useEffect } from 'react'
import Skeleton, { SkeletonTheme } from 'react-loading-skeleton'
import { useGetEnrollmentPersonalizationCompleteData, useSubmitEnrollmentPersonalizationCompleteData } from '@egw/api'
import { Button, GenericError, PieChart } from '@egw/components'
import { useTranslation } from '@egw/i18n'
import { Logger } from '@egw/logger'
import { EnrollmentLayout } from '@egw/layouts'
import { useRouter } from '@egw/router'
import { WEALTH_ROUTES } from '@egw/constants'
import { ROUTES } from '../../../constants'
import { PageHeader } from '../../../components'
import { useValidateRoute } from '../../../hooks/navigation/validate-route'

const log = new Logger('Enrollment - Portfolio Customized')

export const PersonalizationCompletePage: FC = () => {
  const { t } = useTranslation('features-enrollment')
  const { navigate } = useRouter()

  const { data, isLoading, error } = useGetEnrollmentPersonalizationCompleteData()
  const {
    mutate: submit,
    isLoading: isSubmitting,
    error: submitError,
  } = useSubmitEnrollmentPersonalizationCompleteData()

  const riskTolerance = data?.enrollmentData.userInfo.investment.riskTolerance

  useValidateRoute(data?.path)

  const handleLinkClick = async (): Promise<void> => {
    try {
      const response = await submit({
        proceedPersonalizationComplete: true,
      })
      if (response?.path) {
        navigate(response.path)
      }
    } catch (err) {
      log.error('Error submitting enrollment intro data', err)
    }
  }

  return (
    <EnrollmentLayout title="Personalization Complete">
      <div className="egw-container-enroll h-full flex flex-col">
        {/* Loading initial data to make sure the user has access to this screen and retreive data based on user enrollment application */}
        {isLoading ? (
          <SkeletonTheme baseColor="#eeefee" highlightColor="rgba(255, 255, 255, 0.5)">
            <Skeleton count={1} height="100px" className="mb-10" />
            <Skeleton count={5} height="50px" />
          </SkeletonTheme>
        ) : null}

        {/* In some cases initial data can be null, so we assume if it's not loading and there's no error - we show the page */}
        {!isLoading && !error && !submitError ? (
          <>
            <PageHeader title={t('investments.portfolio-customized.title')} />
            {riskTolerance ? (
              <PieChart
                segmentOne={riskTolerance.allocation.equityPercent}
                segmentTwo={riskTolerance.allocation.fixedIncomePercent}
                segmentThree={riskTolerance.allocation.cashEquivalentPercent}
                highlightedSegment={2}
                showPercentage={false}
                showAdditionalOverlays
                highlightAllSegments
                className="md:w-[120px] h-[120px] mt-md"
              />
            ) : null}
            <p className="typo-primary-xl-light text-text-body mt-lg">
              {t('investments.portfolio-customized.portfolio_complete')}
            </p>
            <p className="typo-primary-base-regular text-text-meta mt-2xs">
              {t('investments.portfolio-customized.next_step')}
            </p>

            <div className="flex-1 md:hidden" />

            <Button
              type="button"
              className="w-full md:w-min mt-lg"
              testId="enrollment-continue-button"
              isLoading={isSubmitting}
              onClick={() => {
                void handleLinkClick()
              }}
            >
              {t('common.continue')}
            </Button>
          </>
        ) : null}

        {/* This error handles the case when the initial data is not loaded, for example network error */}
        {error ? (
          <div className="flex-1 flex flex-col justify-start md:justify-center">
            <GenericError errorMessage={t('common.generic_error')} />
          </div>
        ) : null}
      </div>
    </EnrollmentLayout>
  )
}
